import {classNames, _, React, useAsObservableSource, useContext, useEffect, useLocalStore, observer, stringify, Fragment, Popover2} from '@api'
import {Icon, Menu, MenuDivider, MenuItem, Popover, Spinner} from '@blueprintjs/core'
import {BuilderCardWithPopoverContainer, LoadingIndicator, UNASSIGNED, useVipLocalStore} from '@modules'
import {VipLocalStore} from '@modules'
import {EsAggregation} from '@stores/elasticsearch'
import {StoresInstance} from '@stores/StoresInstance'
import {NumberBadge, PrimaryIconButton} from 'centric-ui-components'
import {action, autorun, computed, observable, runInAction} from 'mobx'
import * as css from './GroupByPivot.css'

export class GroupByPivotStore extends VipLocalStore {
  @observable settings = {
    compact: true,
    hideEmptyCells: false,
    expandAll: false
  }

  /**
   * Separate from above settings to avoid instantly rerendering when user clicks menu
   */
  @observable expandAll = false

  constructor(stores: StoresInstance) {
    super(stores)
    this.groupBy.pivotStore = this
    autorun(() => {
      const {settings: {expandAll}} = this
      this.expandAll = expandAll
    }, {delay: 200})
  }

  @observable loading = true
  @observable.ref data: {
    hierarchy: {
      buckets: Array<EsAggregation | any>
    },
    itemCount: { value: number }
  }

  @computed get queryPayload() {
    const {catalog: {esFieldFor_byField, _sourceDynamic, es: {catalogItemFilter}, groupBy: {hierarchyGroups, isProductView}}} = this
    if (isProductView) {
      return {
        size: 0,
        ...catalogItemFilter.filter
      }
    }
    return {
      size: 0,
      aggs: {
        hierarchy: {
          composite: {
            size: 10000,
            sources: hierarchyGroups.reduce((acc, {level, groupName, firstOrderBy}) => {
              acc.push({
                [groupName]: {
                  terms: {
                    field: esFieldFor_byField(groupName, true),
                    order: firstOrderBy.order ?? 'asc',
                    missing_bucket: true
                  }
                }
              })
              return acc
            }, [])
          },
          aggs: {
            items: {
              top_hits: {
                size: 10000,
                ..._sourceDynamic
              }
            }
          }
        },
        ...hierarchyGroups.reduce((acc, {level, groupName}) => {
          const field = esFieldFor_byField(groupName, true)

          return _.assign(acc, {
            [groupName]: {terms: {field, min_doc_count: 0, size: 10000, missing: UNASSIGNED}},
            [`count.${groupName}`]: {value_count: {field}},
            [`${UNASSIGNED}.${groupName}`]: {missing: {field}}
          })
        }, {}),
        itemCount: {
          value_count: {field: '_id'}
        }
      },
      ...catalogItemFilter.filter
    }
  }

  runSearch = async () => {
    const {queryPayload, es, catalog, groupBy} = this
    this.loading = true
    const {aggregations} = await es.search({
      body: queryPayload,
      index: catalog.index,
      meta: `GroupByPivot.runSearch() - ${stringify(groupBy.hierarchy)}`
    }, {
      log: {query: false}
    })

    runInAction(() => {
      this.data = aggregations as any
      this.loading = false
      //console.log(this.data)
    })
  }

  @computed get itemCount() {
    return this.data?.itemCount?.value ?? null
  }

  @computed get distinctValuesByLevel(): Array<Array<string>> {
    const {data, groupBy} = this
    if (_.isEmpty(data)) return []

    return groupBy.hierarchyGroups.reduce((acc, {groupName, firstOrderBy, groupOfflineItems, firstCustomSort}, i) => {
      const distinctValues = _.uniq([...data[groupName]?.buckets?.map(({key, key_as_string}) => key_as_string ?? key), ...Object.keys(groupOfflineItems)]) ?? []
      let orderedValues: string[] = []
      if (!firstCustomSort) {
        orderedValues = distinctValues
      } else {
        const sorted = firstOrderBy.order == 'asc' ? firstCustomSort.order : firstCustomSort.orderDesc ? firstCustomSort.orderDesc : _.reverse(firstCustomSort.order)
        const dict = new Set<string>(sorted)
        orderedValues = [...sorted, ...distinctValues.filter(k => !dict.has(k))]
      }

      return [...acc, orderedValues]
    }, [])
  }

  @computed get countByGroupValue() {
    const {data, distinctValuesByLevel, groupBy} = this
    const counts = {}
    if (data?.hierarchy?.buckets) {
      const {hierarchy} = groupBy
      groupBy.hierarchy.length > 1 && distinctValuesByLevel[1].forEach(lvl1 => {
        return groupBy.hierarchy.length > 2 ? distinctValuesByLevel[2].forEach(lvl2 => {
          const values = data?.hierarchy?.buckets.filter(bucket => bucket.key[hierarchy[1]] === (lvl1 === 'true' ? null : lvl1) && bucket.key[hierarchy[2]] === lvl2)
          counts[lvl1] = {
            ...counts[lvl1],
            [lvl2]: values.reduce((acc, {doc_count}) => acc += doc_count, 0)
          }
        }) : []
      })
    }
    return counts
  }

  expandedRows = observable.set<number>()
  @action onToggleRowExpansion = (index: number) => (e: React.MouseEvent) => {
    const {expandedRows} = this
    expandedRows.has(index) ? expandedRows.delete(index) : expandedRows.add(index)
  }

  expandedColumns = observable.set<{ level: number, index: number }>()

  @action onDoubleClickColumnHeader = (coords: { level: number, index: number }) => (e: React.MouseEvent) => {
    const {expandedColumns} = this
    expandedColumns.has(coords) ? expandedColumns.delete(coords) : expandedColumns.add(coords)
  }

  @computed get countsByLevel(): Array<{ [group: string]: number }> {
    const {data, groupBy} = this
    return groupBy.hierarchy.reduce((acc, v, i) => [...acc, !data || !data[v] ? {} : data[v].buckets.reduce((acc, {key, key_as_string, doc_count}) => _.assign(acc, {[key_as_string ?? key]: doc_count}), {})], [])
  }

  @computed get countsForOffline() {
    const {groupBy: {hierarchy, groupingByName}} = this
    const groups = new Map()
    hierarchy.forEach(h => {
      groups.set(h, groupingByName.get(h).groupOfflineItems || [])
    })
    return groups
  }

  @computed get pivotOfflineItems() {
    const {es, catalog:{filteredOfflineItems, _fieldByName, groupBy: {hierarchy}}} = this

    return _.groupBy(filteredOfflineItems, o => {
      const key = {}
      hierarchy.forEach(h => {
        const field = _fieldByName[h]
        const fieldVal = es.readField(o, h, field.group?.field)
        key[h] = field.def?.type === 'Boolean' ? fieldVal.val ?? UNASSIGNED : fieldVal.valAsString ?? UNASSIGNED
      })
      return stringify(key)
    })
  }

  @computed get detailBuckets() {
    if (_.isEmpty(this.data)) return []
    const {data: {hierarchy: {buckets}}, pivotOfflineItems} = this
    Object.keys(pivotOfflineItems).map(key => {
      const f: any = buckets.find(b => _.isEqual(JSON.parse(key), b.key)),
        offlineItemBucket = pivotOfflineItems[key].map(o => ({_source: o})) ?? [],
        offlineBucketCount = offlineItemBucket?.length || 0
      if (!f) {
        buckets.push({
          key: JSON.parse(key),
          items: ({hits: {hits: [], total: {value: offlineBucketCount}, max_score: null}} as any),
          doc_count: 0,
          offline_count: offlineBucketCount
        })
      } else {
        f.offline_count = offlineBucketCount
      }
    })
    return buckets
  }

  @computed get rowByIndex(): { [row: number]: string } {
    return this.distinctValuesByLevel[0]?.reduce((acc, v, i) => _.assign(acc, {[i]: v}), {}) ?? {}
  }

  @computed get rowByValue(): { [value: string]: number } {
    return this.distinctValuesByLevel[0]?.reduce((acc, v, i) => _.assign(acc, {[v]: i}), {}) ?? {}
  }

  keyFor = (keys: Array<string>) => keys.includes(null) ? null : keys.join('|')

  @computed get bucketByCoordinate(): { [row: number]: { [column: number]: EsAggregation | any } } {
    const {detailBuckets, compoundKeyColumnLookup, keyFor, pivotOfflineItems, rowByValue, offlineKeyFor, groupBy} = this,
      {hierarchy} = groupBy

    const r = detailBuckets.reduce(
      (acc, bucket) => {
        const rowValue = bucket.key[hierarchy[0]],
          row = rowByValue[rowValue !== null ? rowValue : UNASSIGNED],
          columnValues = hierarchy.slice(1).map(group => bucket.key[group]),
          columnKey = keyFor(columnValues),
          column = compoundKeyColumnLookup[columnKey !== null ? columnKey : UNASSIGNED],
          offlineItemsRowColumn = pivotOfflineItems[offlineKeyFor(rowValue, columnValues)]
        // console.log({bucket, rowValue, row, columnValues, column, offlineItemsRowColumn})
        return _.merge(acc, {
          [row]: {[column]: {...bucket, offline: offlineItemsRowColumn ?? []}}
        })
      },
      {}
    )

    return r
  }

  offlineKeyFor = (rowVal, columnsVal) => {
    const {groupBy: {hierarchy}} = this,
      row = _.first(hierarchy),
      columns = hierarchy.slice(1),
      key = {[row]: rowVal}

      columns.forEach((column, i) => key[column] = columnsVal[i])

    return stringify(key)

  }

  @computed get pivotColumnRows(): Array<{ level: number, index: number, value?: string, parents?: string[] }> {
    const {distinctValuesByLevel, groupBy} = this,
      drilldown = (acc = [], depth, parents = []) => {
        const values = distinctValuesByLevel[depth]
        return [
          ...acc,
          ...(!values ? [] : values.reduce(
            depth + 1 == groupBy.hierarchy.length
            ? (columnHeaders, value) => [...columnHeaders, {level: depth, index: columnHeaders.length + acc.length, value, parents}]
            : (children, child) => drilldown(children, depth + 1, [...parents, child]),
            []))
        ]
      }

    const r = groupBy.hierarchy.length > 1
              ? _.range(1, groupBy.hierarchy.length).reduce((acc, level) => drilldown(acc, level), [])
              : [{level: 1, index: 0}]

    return r
  }

  @computed get compoundKeyColumnLookup() {
    const {pivotColumnRows, keyFor} = this

    return pivotColumnRows.reduce((acc, {parents, value}, col) => _.assign(acc, {[keyFor([...(parents ?? []), value])]: col}), {})
  }

  @computed get detailCells() {
    const {rowByIndex, pivotColumnRows} = this
    return _.flatMap(_.range(0, _.keys(rowByIndex).length).map(r => _.range(0, pivotColumnRows.length).map(c => <PivotDetailCell key={`${r}|${c}`} row={r} column={c}/>)))
  }

  @computed get filteredRowIndexes() {
    const {distinctValuesByLevel, settings: {compact}, countsByLevel} = this
    return !distinctValuesByLevel[0]?.length ? [] : distinctValuesByLevel[0].map((f, i) => !compact || countsByLevel[0][f] ? null : i).filter(v => v !== null)
  }

  @action moveGroup = (from: number, to: number) => {
    const {groupBy: {hierarchy}, groupBy} = this
    groupBy.hierarchy.splice(to, 0, hierarchy.splice(from, 1)[0])
  }
}

export const GroupByPivotStoreContext = React.createContext<GroupByPivotStore>(undefined)
export const useGroupByPivotStore = () => useContext(GroupByPivotStoreContext)

export const GroupByPivot = observer<{ className?: string }>(({className}) => {
    const store = useVipLocalStore((ws) => new GroupByPivotStore(ws))
    const {loading, itemCount, distinctValuesByLevel, countByGroupValue, settings: {compact, hideEmptyCells},
        catalog: {isShowingClientPDF, settings: {imageRatio}, groupBy, groupBy: {hierarchy, settings: {showExpandedFirst}}}} = store,
      rows = distinctValuesByLevel[0]?.length ?? 0,
      columns = distinctValuesByLevel[1]?.length ?? 1

    useEffect(() => autorun(async () => {
      await store.runSearch()
    }), [])

    let countSoFar = 0
    //console.log({distinctValuesByLevel, itemCount, loading})

    return (
      <GroupByPivotStoreContext.Provider value={store}>
        {!isShowingClientPDF &&
         <div
			 className={classNames(css.root, className)}
			 data-show-expanded-first={showExpandedFirst}
			 data-image-ratio={imageRatio}
			 data-loading={loading}
			 data-count={itemCount ?? 0}
			 style={{
               '--group-zoom': 1
             } as any}
		 >
			 <LoadingIndicator text='Loading Pivot View...'/>
			 <div className={classNames(css.pivot)}
			      data-type='PivotTable'
			      data-compact={compact}
			      data-hide-empty-cells={hideEmptyCells}
			      data-loading={loading}
			      data-rows={rows}
			      data-columns={columns}
			      style={{
                    '--hierarchy-depth': hierarchy.length,
                    '--rows': rows,
                    '--columns': columns
                  } as any}>
				 <CornerCell/>
               {distinctValuesByLevel.length > 0 && distinctValuesByLevel[0].map((v, i) => <PivotRowHeader key={i} index={i} value={v}/>)}

               {groupBy.hierarchy.length == 1
                ? <div className={css.columnHeaderPlaceholder}/>
                :
                <Fragment>
                  {distinctValuesByLevel.length > 1 && distinctValuesByLevel[1].map((v, i) => {
                    const children = Object.keys(countByGroupValue[v] ?? {}).length
                    countSoFar += i == 0 ? 0 : children
                    return <PivotColumnHeader key={i} column={countSoFar} children={children} level={1} index={i} value={v}/>
                  })}
                  {groupBy.hierarchy.length > 1 && _.range(2, groupBy.hierarchy.length).map(level => <PivotColumnsRow level={level} key={level}/>)}
                </Fragment>}
				 <PivotDetailCells/>
               {/*{distinctValuesByLevel.length > 2 && distinctValuesByLevel[2].map((v, i) => <PivotColumnHeader key={i} level={2} index={i} value={v}/>)}*/}
			 </div>
		 </div>}

      </GroupByPivotStoreContext.Provider>
    )
  }
)

const CornerCell = observer(() => {
  const {} = useGroupByPivotStore()

  return (
    <div className={classNames(css.cornerCell)}
         data-type='CornerCell'>
      <PivotSettingsMenuButton/>
    </div>)
})

const PivotSettingsMenuButton = observer(() => {
  const {
    settings, settings: {compact, expandAll, hideEmptyCells}, moveGroup,
    groupBy: {hierarchy, hierarchyGroups}
  } = useGroupByPivotStore()

  return (
    <Popover2 className={classNames(css.cornerCellWrapper)}
             interactionKind='click'
             position='bottom-right'
             popoverClassName={css.cornerCellMenuPopover}
             content={
               <Menu>
                 {hierarchyGroups.map((g, level) =>
                   <MenuItem key={level}
                             popoverProps={{openOnTargetFocus: false, ...MenuItem.defaultProps.popoverProps}}
                             className={css.groupMenuItem}
                             icon={<span className={css.level}>{level + 1}</span>}
                             text={g.fullTitle}>
                     {/*<MenuItem text='Collapse All' icon={level == 0 ? 'double-chevron-left' : 'double-chevron-up'}/>*/}
                     {/*<MenuItem text='Expand All' icon={level == 0 ? 'double-chevron-right' : 'double-chevron-down'}/>*/}
                     {/*<MenuDivider/>*/}
                     <MenuItem text='Remove this Group' icon='trash' onClick={() => hierarchy.splice(level, 1)}/>
                     {hierarchyGroups.length > 1 &&
			          <Fragment>
                        {_.range(0, hierarchyGroups.length).filter(i => i != level).map(i => <MenuItem key={`move-to-${i}`} icon='move' text={`Move to Level ${i + 1}`} onClick={e => moveGroup(level, i)}/>)}
			          </Fragment>}

                     <MenuItem text='Switch group to...'>
                       <MenuItem text='other groups'/>
                     </MenuItem>
                   </MenuItem>)}
                 <MenuItem text='Drill Down' icon='fork' popoverProps={{openOnTargetFocus: false, ...MenuItem.defaultProps.popoverProps}}>
                   <MenuItem text='Remaining groups'/>
                 </MenuItem>

                 <MenuDivider/>

                 <MenuItem onClick={() => settings.expandAll = true}
                           shouldDismissPopover
                           text={'Expand All'}
                           icon={'double-chevron-right'}/>

                 <MenuItem onClick={() => settings.expandAll = false}
                           shouldDismissPopover
                           text={'Collapse All'}
                           icon={'double-chevron-left'}/>

                 <MenuDivider/>

                 <MenuItem onClick={() => settings.compact = !compact}
                           shouldDismissPopover={false}
                           text='Compact Empty Cells'
                           icon={compact ? 'tick' : 'blank'}/>

                 <MenuItem onClick={() => settings.hideEmptyCells = !hideEmptyCells}
                           shouldDismissPopover={false}
                           text='Hide Empty Cells'
                           icon={hideEmptyCells ? 'tick' : 'blank'}/>
               </Menu>
             }
             data-type='PivotSettingsMenuButton'>
      <PrimaryIconButton icon={<Icon icon='settings'/>}/>
    </Popover2>)
})

const PivotRowHeader = observer<{ className?: string, index: number, value: string }>(({className, index, value}) => {
  const {countsByLevel, countsForOffline, onToggleRowExpansion, expandAll, expandedRows, groupBy} = useGroupByPivotStore(),
    count = countsByLevel[0][value] ?? 0,
    {firstGroup} = groupBy,
    offlineGroup = countsForOffline.get(firstGroup.fieldName),
    offlineCount = offlineGroup?.[value]?.length ?? 0

  return (
    <div className={classNames(css.rowHeader, className)}
         data-level={0}
         data-count={count + offlineCount}
         data-type='PivotRowHeader'
         data-row={index}
         data-group={value}
         onDoubleClick={onToggleRowExpansion(index)}
         style={{
           '--index': index
         } as any}
    >
      <PrimaryIconButton onClick={onToggleRowExpansion(index)} icon={<Icon icon={expandAll || expandedRows.has(index) ? 'chevron-left' : 'chevron-right'}/>}/>
      <label className={css.groupName}>{groupBy.hierarchyGroups[0].fullTitle}</label>
      <label className={css.groupValue}>{_.startCase(firstGroup.labelForValue(value))}</label>
      <NumberBadge children={count} className={css.count}/>
      {offlineCount > 0 && <NumberBadge children={offlineCount} className={classNames(css.offlineCount, css.count)}/>}
    </div>)
})

/**
 * A row of columns at the top of the pivot.  Requires a hierarchy of length > 1
 * @param level
 * @constructor
 */
const PivotColumnsRow = observer<{ level: number }>(function PivotColumnsRow({level}) {
  const {groupBy} = useGroupByPivotStore()

  if (groupBy.hierarchy.length <= level) { throw new Error(`Invalid use of PivotColumnsRow - level must be less than ${groupBy.hierarchy.length}`)}

  const {pivotColumnRows} = useGroupByPivotStore()

  return <Fragment>
    {pivotColumnRows.map((v, i) => <PivotColumnHeader key={i} {...v} />)}
  </Fragment>
})

const PivotColumnHeader = observer<{ className?: string, level: number, index: number, value?: string, parents?: string[], column?: number, children?: number }>(({className, level, index, value, parents, column, children}) => {
  //console.log({value, level, index, parents, column})

  const {countsByLevel, countsForOffline, groupBy} = useGroupByPivotStore(),
    grouping = groupBy.hierarchyGroups[level],
    count = countsByLevel.length > level ? (countsByLevel[level][value] ?? 0) : 0,
    offlineGroup = countsForOffline.get(grouping.fieldName),
    offlineCount = offlineGroup?.[value]?.length ?? 0

  return (
    <div className={classNames(css.columnHeader, className)}
         data-level={level}
         data-parent={_.last(parents) ?? level}
         data-type='PivotColumnHeader'
         data-index={index}
         data-count={count + offlineCount}
         style={{
           '--column': column != 0 ? column : index,
           '--column-span': children ?? 0
         } as any}
         data-group={value}>
      {/*<PrimaryIconButton icon={<Icon icon='chevron-down'/>}/>*/}
      <label className={css.groupName}>{groupBy.hierarchyGroups[level].fullTitle}</label>
      <label className={css.groupValue}>{grouping.labelForValue(value)}</label>
      <NumberBadge children={count}/>
      {offlineCount > 0 && <NumberBadge children={offlineCount} className={classNames(css.offlineCount, css.count)}/>}
    </div>)
})

const PivotDetailCells = observer<{ className?: string, }>(({className}) => {
  const {detailCells} = useGroupByPivotStore()

  return <Fragment>
    {detailCells}
  </Fragment>
})

interface PivotDetailCellProps {
  className?: string,
  row: number,
  column: number
}

class PivotDetailCellStore {
  constructor(public pivot: GroupByPivotStore, public props: PivotDetailCellProps) {
    this.everExpanded = this.expanded
  }

  @computed get cell() {
    const {pivot: {bucketByCoordinate}, props: {row, column}} = this
    return bucketByCoordinate[row] ? bucketByCoordinate[row][column] : null
  }

  @computed get cards() {
    return this.cell?.items?.hits?.hits
  }

  @computed get offlineCards() {
    return this.cell?.offline
  }

  @observable _expanded = false

  @computed get expanded() {
    const {_expanded, pivot: {expandAll, expandedRows, expandedColumns}, props: {row, column}} = this
    return _expanded || expandAll || expandedRows.has(row) || expandedColumns.has(column)
  }

  @observable everVisible = true // Performance is bad trying to dynamically render the cells
  @observable everExpanded = false

  @computed get key() {
    const {cell, props: {row, column}} = this
    return cell ? stringify(cell.key) : `${row},${column}`
  }

  @action onDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation()

    if (!this._expanded || e.currentTarget.hasAttribute('data-expanded')) {
      this.toggleExpand()
    }
  }

  @action toggleExpand = () => {
    const everExpanded = this.everVisible || this._expanded
    if (!this.everExpanded && everExpanded) {
      this.loadingMore = true
    }

    setTimeout(action(() => {
      this._expanded = !this._expanded
      this.everExpanded = everExpanded
    }), 20)
  }

  @computed get count() { return this.cell?.doc_count ?? 0}

  @computed get offlineCount() { return this.cell?.offline_count || 0 }

  itemsPerPage = 50

  @computed get totalLoaded() {
    const {pagesLoaded, offlineCount, count, itemsPerPage} = this
    return Math.min((count + (offlineCount ?? 0)), pagesLoaded * itemsPerPage)
  }

  @observable loadingMore = false

  @action loadNextPage = (e: React.MouseEvent) => {
    const {loadingMore, count, totalLoaded, _expanded} = this
    if (!loadingMore && _expanded && totalLoaded < count) {
      this.loadingMore = true
      setTimeout(() => this.pagesLoaded++, 50)
    }
    e.stopPropagation()
  }
  @observable pagesLoaded = 1
}

export const PivotDetailCellStoreContext = React.createContext<PivotDetailCellStore>(undefined)
export const usePivotDetailCellStore = () => useContext(PivotDetailCellStoreContext)

const PivotDetailCell = observer<{ className?: string, row: number, column: number }>(({className, row, column}) => {
  const pivotStore = useGroupByPivotStore(),
    obsProps = useAsObservableSource({row, column}),
    store = useLocalStore(() => new PivotDetailCellStore(pivotStore, obsProps)),
    {expanded, loadingMore, loadNextPage, toggleExpand, totalLoaded, everVisible, key, everExpanded, cell, cards, onDoubleClick, offlineCount, count, pivot: {expandAll}} = store
  //[{isIntersecting}, ref] = useIntersectionObserver()
  useEffect(() => {
    store.everExpanded = store.everExpanded || expanded
  }, [expanded])

  // useEffect(() => {
  //   if (!everVisible && isIntersecting) {
  //     //console.log(`became visible: ${row}, ${column}`)
  //     store.everVisible = true
  //   }
  // }, [isIntersecting])

  /*useLayoutEffect(() => {
    store.loadingMore = false
  }, [loadingMore])*/

  return (
    <PivotDetailCellStoreContext.Provider value={store}>
      <div className={classNames(css.detailCell, className)}
        //ref={cell ? ref : undefined}
           data-type='PivotDetailCell'
           data-count={(cell?.doc_count ?? 0) + offlineCount}
           data-row={row}
           data-expanded={expanded}
           data-loading={loadingMore}
           data-column={column}
           data-key={key}
           onClick={() => console.log({row, column, cell, store})}
           onDoubleClick={onDoubleClick}
           style={{
             '--count': count + offlineCount,
             '--row': row,
             '--column': column // Indexes are 1 indexed in CSS grid and we also have to +1 for the row labels
           } as any}>
        {/*{stringify(item)}*/}
        {count > 0 && <div className={css.countContainer}>
			<NumberBadge children={
              <span className={css.countLabel}>
          <span>{expanded && totalLoaded < count ? `${totalLoaded} / ${count}` : count}</span>
                {loadingMore && <Spinner size={20}/>}
        </span>}
			             className={css.count}
			             onClick={loadNextPage}
			             onDoubleClick={onDoubleClick}/>
		</div>}
        {offlineCount > 0 && <div className={classNames(css.countContainer, {[css.offlineCountContainer]: count !== 0, [css.onlyOfflineCountContainer]: count === 0})}>
			<NumberBadge children={
              <span className={css.countLabel}>
          <span>{offlineCount}</span>
        </span>}
			             className={css.count}
			/>
		</div>}

        {everVisible && (expanded || everExpanded) && <PivotDetailCellCards/>}
      </div>
    </PivotDetailCellStoreContext.Provider>
  )
})

const PivotDetailCellCards = observer(() => {
  const store = usePivotDetailCellStore(),
    {cards, offlineCards, totalLoaded} = store

  useEffect(() => {
    store.loadingMore = false
  }, [store.totalLoaded])

  return (
    <div className={css.detailCards}>
      {offlineCards?.length > 0 && offlineCards.map(offline => <BuilderCardWithPopoverContainer key={offline._id} item={offline}/>)}
      {_.take(cards, totalLoaded).map(({_source}, i) => <BuilderCardWithPopoverContainer key={i} item={_source}/>)}
    </div>)
})




// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/GroupByPivot/GroupByPivot.tsx